import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

export function Counter({ to = 0, suffix = "", duration = 1.6, prefix = "", testId }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let start = null;
    const from = 0;
    const ease = (t) => 1 - Math.pow(1 - t, 3);
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min(1, (ts - start) / (duration * 1000));
      setVal(Math.round(from + (to - from) * ease(p)));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [inView, to, duration]);

  return (
    <span ref={ref} data-testid={testId} className="font-display tabular-nums">
      {prefix}{val}{suffix}
    </span>
  );
}

export const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

export function Section({ id, className = "", children, eyebrow, title, kicker, center }) {
  return (
    <section id={id} className={`relative py-24 md:py-32 ${className}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {(eyebrow || title || kicker) && (
          <motion.div {...fadeUp} className={`max-w-3xl ${center ? "mx-auto text-center" : ""} mb-14`}>
            {eyebrow && (
              <p className="text-xs tracking-[0.25em] uppercase font-semibold text-[#3B82F6] mb-4">{eyebrow}</p>
            )}
            {title && (
              <h2 className="font-display text-3xl md:text-5xl tracking-tight font-semibold leading-[1.05]">{title}</h2>
            )}
            {kicker && <p className="mt-5 text-[#D1D5DB] text-lg leading-relaxed">{kicker}</p>}
          </motion.div>
        )}
        {children}
      </div>
    </section>
  );
}

export function PrimaryButton({ children, className = "", ...props }) {
  return (
    <button
      {...props}
      className={`inline-flex items-center justify-center gap-2 bg-[#3B82F6] hover:bg-[#2563EB] text-white font-medium px-6 md:px-8 py-3.5 rounded-lg transition-all duration-300 shadow-[0_0_20px_rgba(59,130,246,0.25)] hover:shadow-[0_0_30px_rgba(59,130,246,0.5)] disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
    >
      {children}
    </button>
  );
}

export function OutlineButton({ children, className = "", ...props }) {
  return (
    <button
      {...props}
      className={`inline-flex items-center justify-center gap-2 bg-transparent border border-white/15 hover:border-white/40 text-white font-medium px-6 md:px-8 py-3.5 rounded-lg transition-all duration-300 backdrop-blur-sm hover:bg-white/5 ${className}`}
    >
      {children}
    </button>
  );
}

export function GlassCard({ className = "", children, ...rest }) {
  return (
    <div {...rest} className={`relative rounded-2xl bg-white/[0.03] border border-white/10 hover:border-white/20 transition-colors p-6 md:p-8 ${className}`}>
      {children}
    </div>
  );
}

export function Field({ label, hint, children, testId }) {
  return (
    <label className="block" data-testid={testId}>
      <span className="block text-xs tracking-[0.18em] uppercase text-[#9CA3AF] mb-2">{label}</span>
      {children}
      {hint && <span className="block mt-1 text-xs text-[#9CA3AF]">{hint}</span>}
    </label>
  );
}

export const inputCls =
  "w-full bg-white/5 border border-white/10 focus:border-[#3B82F6] focus:ring-1 focus:ring-[#3B82F6]/40 text-white placeholder:text-white/30 rounded-lg px-4 py-3 outline-none transition-colors";

export function Toast({ msg }) {
  if (!msg) return null;
  return (
    <div data-testid="toast" className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[200] bg-[#1A1A1A] border border-white/10 text-white text-sm px-5 py-3 rounded-lg shadow-2xl">
      {msg}
    </div>
  );
}
